import { z } from "zod";

// Approval request states
export const ApprovalStatus = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

export type ApprovalStatus = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

// Tool call types that require approval
export const ApprovalToolType = {
  CREATE_JOB: "create_job",
  CREATE_TASK: "create_task",
} as const;

export type ApprovalToolType = (typeof ApprovalToolType)[keyof typeof ApprovalToolType];

// Base approval request schema
export const ApprovalRequestSchema = z.object({
  // tool: z.object({
  //   id: z.string(),
  //   type: z.string(),
  //   function: z.object({ name: z.string(), arguments: z.record(z.string(), z.any()) }).optional(),
  //   retrieval: z.object({ query: z.string() }).optional(),
  // }),
  status: z.enum([ApprovalStatus.PENDING, ApprovalStatus.APPROVED, ApprovalStatus.REJECTED]),
  requested_at: z.iso.datetime(),
  resolved_at: z.iso.datetime().optional(),
  resolved_by: z
    .object({
      id: z.string(),
      type: z.string(),
      name: z.string(),
    })
    .optional(),
});

export type ApprovalRequest = z.infer<typeof ApprovalRequestSchema>;

// Specific tool schemas
export const CreateJobApprovalSchema = z.object({
  tool_type: z.literal(ApprovalToolType.CREATE_JOB),
  tool_args: z.object({
    title: z.string(),
    description: z.string(),
    client_id: z.uuid(),
    status: z.enum(["draft", "published", "archived"]),
  }),
  status: z.enum([ApprovalStatus.PENDING, ApprovalStatus.APPROVED, ApprovalStatus.REJECTED]),
  requested_at: z.iso.datetime(),
  resolved_at: z.iso.datetime().optional(),
  resolved_by: z
    .object({
      id: z.string(),
      type: z.string(),
      name: z.string(),
    })
    .optional(),
});

export const CreateTaskApprovalSchema = z.object({
  tool_type: z.literal(ApprovalToolType.CREATE_TASK),
  tool_args: z.object({
    title: z.string(),
    description: z.string(),
    assigned_to: z.array(
      z.object({
        type: z.enum(["user"]),
        id: z.uuid(),
      }),
    ),
    due_at: z.iso.datetime().optional(),
  }),
  status: z.enum([ApprovalStatus.PENDING, ApprovalStatus.APPROVED, ApprovalStatus.REJECTED]),
  requested_at: z.iso.datetime(),
  resolved_at: z.iso.datetime().optional(),
  resolved_by: z
    .object({
      id: z.string(),
      type: z.string(),
      name: z.string(),
    })
    .optional(),
});

export type CreateJobApproval = z.infer<typeof CreateJobApprovalSchema>;
export type CreateTaskApproval = z.infer<typeof CreateTaskApprovalSchema>;

// Union type for all approval types
export type SpecificApprovalRequest = CreateJobApproval | CreateTaskApproval;

// Message meta schema for approval messages
export const ApprovalMessageMetaSchema = z.object({
  approval_data: ApprovalRequestSchema,
});

export type ApprovalMessageMeta = z.infer<typeof ApprovalMessageMetaSchema>;

// Tool execution result schema
export const ToolExecutionResultSchema = z.object({
  success: z.boolean(),
  result: z.any().optional(),
  error: z.string().optional(),
});

export type ToolExecutionResult = z.infer<typeof ToolExecutionResultSchema>;

export type AgentChatEvent = {
  message_id?: string;
  // conversation_id: string;
  content: string;
  role: string;
  type:
    | "status"
    | "heartbeat"
    | "chat_stream"
    | "chat_end"
    | "tool_call"
    | "tool_call_error"
    | "tool_stream"
    | "conversation_created"
    | "approval_request"
    | "approval_result"
    | "tool_call_attempt"
    | "tool_response"
    | "runtime_error"
    | "signal";
  tools: ToolCall[];
  meta: {
    approval_data?: ApprovalRequest;
    tool_response?: { tool_id: string };
    reasoning?: string;
    reasoning_reset?: boolean;
    record_search_results?: {
      kind: "record_search_results";
      total: number;
      rows: Array<Record<string, unknown>>;
    };
    search_replay?: {
      id: string;
      conversation_id: string;
      origin_message_id: string | null;
      tool_call_id: string;
      tool_name: string;
      adapter: "boolean" | "candidate_role_fit" | "record";
      label: string;
      replay_status: "ready" | "preview_only" | "unsupported";
      replay_reason: string | null;
      capability: unknown;
    };
  };
  created_at: string;
};

export function resolveAgentChatEndReasoning(
  previousReasoning: string | undefined,
  meta: Pick<AgentChatEvent["meta"], "reasoning" | "reasoning_reset"> | undefined,
): string | undefined {
  if (meta?.reasoning_reset) return undefined;
  return typeof meta?.reasoning === "string" ? meta.reasoning : previousReasoning;
}

export type ToolCall = {
  id: string;
  readable_name?: string;
  field_labels?: Record<string, string>;
  record_references?: Record<string, string>;
  intent?: string;
  type: "function" | "code_interpreter" | "retrieval";
  function?: { name: string; arguments: Record<string, any> };
  retrieval?: { query: string };
};

export type SerializedToolCall = {
  id: string;
  readable_name?: string;
  field_labels?: Record<string, string>;
  record_references?: Record<string, string>;
  intent?: string;
  type: "function" | "code_interpreter" | "retrieval";
  function?: { name: string; arguments: Record<string, any> };
  retrieval?: { query: string };
};
